/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Velocity Sprint Task Generation
 * 
 * Generates Velocity Sprint workflow task issues for releases using an LLM prompt
 * with access to the issue_manager tool.
 */

import { Issue } from '@shared/types/issues';
import { LLMPromptRequest } from '@shared/types/requests/llm';
import { authenticatedFetch } from '@shared/lib/api';

export type VelocitySprintResult = { success: boolean; result?: string; error?: string };

interface VelocitySprintPhase {
  id: string;
  name: string;
  description: string;
  durationDays: number;
  color: string;
}

/**
 * Generate Velocity Sprint task issues for releases
 * Uses an LLM prompt with the issue_manager tool to create tasks idempotently.
 */
export async function generateVelocitySprintTasks(
  releases: Issue[],
  activeProduct: any,
  workflowPhases: VelocitySprintPhase[]
): Promise<VelocitySprintResult> {
  try {
    if (releases.length === 0) {
      return {
        success: false,
        error: 'No releases found to generate Velocity Sprint tasks for'
      };
    }

    const releaseSummary = releases.map(r => ({
      id: r.id,
      title: r.title,
      status: r.status,
      version: r.version,
    }));

    const phaseDescriptions = workflowPhases.map((p, i) => 
      `${i + 1}. ${p.name}: ${p.description}`
    ).join('\n');

    const promptRequest: LLMPromptRequest = {
      promptName: 'Generate Velocity Sprint Tasks',
      params: {
        releases: JSON.stringify(releaseSummary, null, 2),
        productId: activeProduct.id,
        productTitle: activeProduct.title,
        workflowPhases: phaseDescriptions,
        activeProduct: activeProduct,
      },
      returnRawText: true,
      // Note: disableParallelToolUse is configured in the prompt definition (prompts.json)
      velocityRunContext: {
        type: 'quick_request' as const,
        entityType: 'issue' as const,
        entityId: 'velocity-sprint-tasks',
        productId: activeProduct?.id,
        requestType: 'velocity_sprint_generation',
      },
    };

    console.log('[Velocity Sprint Service] Generating sprint tasks for releases:', {
      releaseCount: releases.length,
      productId: activeProduct.id,
    });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();

    console.log('[Velocity Sprint Service] Sprint task generation completed successfully');
    return {
      success: true,
      result: result,
    };
  } catch (error: any) {
    console.error('[Velocity Sprint Service] Error generating sprint tasks:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to generate Velocity Sprint tasks'
    };
  }
}
